// =====================================================================
// BoardView (Quadros Q1 + PRO / S59-S60) — o quadro: colunas + cards com
// drag-drop (react-beautiful-dnd) + realtime (Socket.io). PRO: visões
// Quadro/Calendário/Tabela, filtros+busca, stats, face de card rica
// (capa/prioridade/prazo/membros/progresso/vínculos), etiquetas, automações, sino.
// =====================================================================
import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper, Typography, IconButton, Tooltip, TextField, Button, CircularProgress, Box, MenuItem, Menu, Checkbox, Chip
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import WallpaperIcon from "@material-ui/icons/Wallpaper";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import TableChartIcon from "@material-ui/icons/TableChart";
import LabelIcon from "@material-ui/icons/Label";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import SubjectIcon from "@material-ui/icons/Subject";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventIcon from "@material-ui/icons/Event";
import CheckBoxOutlinedIcon from "@material-ui/icons/CheckBoxOutlined";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import {
  getBoard, createList, removeList, reorderLists, createCard, moveCard, boardErrorMessage, fmtDateBR,
  LINK_KINDS, PRIORITIES, priorityMeta, dueMeta, checklistProgress, fmtMoneyBR,
  archiveCard, updateList, boardBgStyle
} from "../../services/boardService";
import CardModal from "./CardModal";
import BoardBackgroundDialog from "./BoardBackgroundDialog";
import AttachmentThumb from "./AttachmentThumb";
import UserAvatar from "./UserAvatar";
import CalendarView from "./CalendarView";
import TableView from "./TableView";
import LabelManagerDialog from "./LabelManagerDialog";
import AutomationsDialog from "./AutomationsDialog";
import NotificationsBell from "./NotificationsBell";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, display: "flex", padding: theme.spacing(1), overflow: "hidden", minHeight: 0 },
  board: { display: "flex", gap: theme.spacing(1.5), alignItems: "flex-start", overflowX: "auto", padding: theme.spacing(1), flex: 1, ...theme.scrollbarStyles },
  column: { width: 280, minWidth: 280, background: theme.palette.background.default, borderRadius: 10, padding: theme.spacing(1), display: "flex", flexDirection: "column", maxHeight: "100%", border: `1px solid ${theme.palette.divider}` },
  colHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: theme.spacing(0.5, 0.5), fontWeight: 700 },
  cards: { minHeight: 12, display: "flex", flexDirection: "column", gap: theme.spacing(1.25), overflowY: "auto", padding: 2 },
  card: { borderRadius: 8, background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, cursor: "pointer", overflow: "hidden", height: "auto", flexShrink: 0 },
  cardBody: { padding: theme.spacing(1.25, 1.5) },
  addCard: { padding: theme.spacing(0.75, 1), color: theme.palette.text.secondary, cursor: "pointer", borderRadius: 8, fontSize: "0.85rem", "&:hover": { background: theme.palette.action.hover } },
  addColumn: { width: 250, minWidth: 250, paddingTop: theme.spacing(1) },
  collapsedCol: { width: 44, minWidth: 44, height: 200, borderRadius: 10, border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.default, display: "flex", flexDirection: "column", alignItems: "center", padding: theme.spacing(1, 0), cursor: "pointer", gap: 6 },
  filters: { display: "flex", gap: theme.spacing(1), alignItems: "center", flexWrap: "wrap", padding: theme.spacing(0.5, 1) },
  statsBar: { display: "flex", gap: theme.spacing(1.5), alignItems: "center", padding: theme.spacing(0, 1), color: theme.palette.text.secondary, flexWrap: "wrap" },
  avatar: { width: 22, height: 22, borderRadius: "50%", color: "#fff", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }
}));

const BoardView = ({ boardId, onBack }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [addingCardListId, setAddingCardListId] = useState(null);
  const [newCardTitle, setNewCardTitle] = useState("");
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  // PRO
  const [view, setView] = useState("board"); // board|calendar|table
  const [search, setSearch] = useState("");
  const [filterMember, setFilterMember] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [showDone, setShowDone] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [labelMgrOpen, setLabelMgrOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [bgDialogOpen, setBgDialogOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [listMenuAnchor, setListMenuAnchor] = useState(null);
  const [listMenuList, setListMenuList] = useState(null);
  const [bgImageUrl, setBgImageUrl] = useState("");
  const theme = useTheme();
  const refetchTimer = useRef(null);
  const bgPathRef = useRef("");

  const fetchBoard = useCallback(async () => {
    try {
      const data = await getBoard(boardId, showArchived);
      setBoard(data);
    } catch (e) {
      toast.error(boardErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [boardId, showArchived]);

  useEffect(() => { setLoading(true); fetchBoard(); }, [fetchBoard]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/", { params: { pageNumber: 1 } });
        setUsers(Array.isArray(data && data.users) ? data.users : []);
      } catch { setUsers([]); }
    })();
  }, []);

  useEffect(() => {
    if (!socket || !user) return undefined;
    const companyId = user.companyId;
    const onEvt = () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(fetchBoard, 350);
    };
    socket.on(`company-${companyId}-board-${boardId}`, onEvt);
    return () => {
      socket.off(`company-${companyId}-board-${boardId}`, onEvt);
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    };
  }, [socket, user, boardId, fetchBoard]);

  // fundo personalizado (wallpaper): busca a imagem autenticada → object URL (só re-baixa se o caminho mudar)
  useEffect(() => {
    const isCustom = board && board.background === "custom";
    const path = isCustom ? (board.settings && board.settings.bgImage) || "" : "";
    if (!isCustom || !path) { setBgImageUrl(""); bgPathRef.current = ""; return undefined; }
    if (path === bgPathRef.current) return undefined;
    bgPathRef.current = path;
    let active = true;
    (async () => {
      try {
        const resp = await api.get(`/boards/${boardId}/background`, { responseType: "blob" });
        const url = window.URL.createObjectURL(resp.data);
        if (active) setBgImageUrl(url);
      } catch { if (active) setBgImageUrl(""); }
    })();
    return () => { active = false; };
  }, [boardId, board]);

  const filtersActive = !!(search.trim() || filterMember || filterPriority || !showDone);
  const matches = useCallback((c) => {
    if (!showDone && c.done) return false;
    if (search.trim() && !String(c.title || "").toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (filterMember) {
      const fm = Number(filterMember);
      const inMembers = Array.isArray(c.members) && c.members.map(Number).includes(fm);
      if (Number(c.assignedUserId) !== fm && !inMembers) return false;
    }
    if (filterPriority && String(c.priority || "") !== filterPriority) return false;
    return true;
  }, [showDone, search, filterMember, filterPriority]);

  const onDragEnd = async (result) => {
    const { source, destination, type } = result;
    if (!destination || !board) return;
    if (type === "COLUMN") {
      if (source.index === destination.index) return;
      const lists = Array.from(board.lists || []);
      const [m] = lists.splice(source.index, 1);
      lists.splice(destination.index, 0, m);
      setBoard({ ...board, lists });
      try { await reorderLists(boardId, lists.map((l) => l.id)); } catch { fetchBoard(); }
      return;
    }
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const cardId = Number(result.draggableId.replace("card-", ""));
    const fromListId = Number(source.droppableId.replace("list-", ""));
    const toListId = Number(destination.droppableId.replace("list-", ""));
    const lists = (board.lists || []).map((l) => ({ ...l, cards: Array.from(l.cards || []) }));
    const fromList = lists.find((l) => Number(l.id) === fromListId);
    const toList = lists.find((l) => Number(l.id) === toListId);
    if (!fromList || !toList) return;
    const [moved] = fromList.cards.splice(source.index, 1);
    toList.cards.splice(destination.index, 0, moved);
    setBoard({ ...board, lists });
    try {
      await moveCard(cardId, { toListId, toIndex: destination.index });
    } catch (e) {
      toast.error(boardErrorMessage(e));
      fetchBoard();
    }
  };

  const handleAddCard = async (listId) => {
    const title = newCardTitle.trim();
    if (!title) return;
    try { await createCard(listId, { title }); setNewCardTitle(""); fetchBoard(); }
    catch (e) { toast.error(boardErrorMessage(e)); }
  };
  const handleAddColumn = async () => {
    const name = newColumnName.trim();
    if (!name) { setAddingColumn(false); return; }
    try { await createList(boardId, { name }); setNewColumnName(""); setAddingColumn(false); fetchBoard(); }
    catch (e) { toast.error(boardErrorMessage(e)); }
  };
  const handleRemoveList = async (boardList) => {
    if (!window.confirm(`Excluir a coluna "${boardList.name}" e os cards dela?`)) return;
    try { await removeList(boardList.id); fetchBoard(); } catch (e) { toast.error(boardErrorMessage(e)); }
  };
  const toggleCollapse = (listId) => setCollapsed((prev) => {
    const n = new Set(prev); if (n.has(listId)) n.delete(listId); else n.add(listId); return n;
  });
  const openListMenu = (e, list) => { e.stopPropagation(); setListMenuAnchor(e.currentTarget); setListMenuList(list); };
  const closeListMenu = () => { setListMenuAnchor(null); setListMenuList(null); };
  const handleRenameList = async (list) => {
    closeListMenu();
    if (!list) return;
    const name = window.prompt("Novo nome da coluna:", list.name);
    if (name === null) return;
    const t = name.trim(); if (!t) return;
    try { await updateList(list.id, { name: t }); fetchBoard(); } catch (e) { toast.error(boardErrorMessage(e)); }
  };
  const handleArchiveListCards = async (list) => {
    closeListMenu();
    const cards = (list && list.cards) || [];
    if (!cards.length) return;
    if (!window.confirm(`Arquivar os ${cards.length} cards da coluna "${list.name}"?`)) return;
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const c of cards) { /* eslint-disable-next-line no-await-in-loop */ await archiveCard(c.id); }
      fetchBoard();
    } catch (e) { toast.error(boardErrorMessage(e)); }
  };

  if (loading) {
    return (
      <MainContainer>
        <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 300 }}><CircularProgress /></Box>
      </MainContainer>
    );
  }
  if (!board) {
    return (
      <MainContainer>
        <Box p={4} display="flex" style={{ gap: 12 }} alignItems="center">
          <Button onClick={onBack} startIcon={<ArrowBackIcon />}>Voltar</Button>
          <Typography>Quadro não encontrado.</Typography>
        </Box>
      </MainContainer>
    );
  }

  const lists = board.lists || [];
  const allCards = lists.flatMap((l) => l.cards || []);
  const stats = {
    total: allCards.length,
    done: allCards.filter((c) => c.done).length,
    overdue: allCards.filter((c) => c.dueDate && !c.done && new Date(c.dueDate) < new Date()).length
  };
  const pipelineValue = allCards.reduce((s, c) => s + (Number(c.quoteTotal) || 0), 0);
  // board "filtrado" pras visões
  const viewBoard = { ...board, lists: lists.map((l) => ({ ...l, cards: (l.cards || []).filter(matches) })) };
  const colBg = board.background ? (theme.palette.type === "dark" ? "rgba(18,22,30,0.96)" : "rgba(255,255,255,0.96)") : undefined;
  const boardContainerStyle = board.background === "custom"
    ? (bgImageUrl ? { backgroundImage: `url("${bgImageUrl}")`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "local" } : {})
    : boardBgStyle(board.background);

  const renderCardFace = (card) => {
    const due = dueMeta(card.dueDate, card.done);
    const pm = priorityMeta(card.priority);
    const prog = checklistProgress(card.checklist);
    const memberIds = Array.isArray(card.members) ? card.members : [];
    const presentLinks = LINK_KINDS.filter((k) => card[`${k.kind}Id`]);
    return (
      <>
        {card.coverImageId ? (
          <AttachmentThumb attachmentId={card.coverImageId} style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} />
        ) : card.coverColor ? (
          <div style={{ height: 6, background: card.coverColor }} />
        ) : null}
        <div className={classes.cardBody} style={{ opacity: card.done ? 0.6 : 1 }}>
          {Array.isArray(card.labels) && card.labels.length > 0 && (
            <Box display="flex" style={{ gap: 4, marginBottom: 4, flexWrap: "wrap" }}>
              {card.labels.map((lb, i) => (
                <span key={i} title={lb.name || ""} style={{ height: lb.name ? 14 : 6, minWidth: lb.name ? "auto" : 28, padding: lb.name ? "0 6px" : 0, borderRadius: 3, background: lb.color || "#90a4ae", color: "#fff", fontSize: 10, display: "inline-flex", alignItems: "center" }}>
                  {lb.name || ""}
                </span>
              ))}
            </Box>
          )}
          <Typography variant="body2" style={{ textDecoration: card.done ? "line-through" : "none" }}>{card.title}</Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            {card.priority ? <span title={`Prioridade: ${pm.label}`} style={{ width: 8, height: 8, borderRadius: "50%", background: pm.color, display: "inline-block" }} /> : null}
            {card.description ? <SubjectIcon titleAccess="Tem descrição" style={{ fontSize: 16, color: "#90a4ae" }} /> : null}
            {due ? (
              <span style={{ fontSize: 11, color: "#fff", background: due.color, borderRadius: 4, padding: "1px 6px", display: "inline-flex", alignItems: "center", gap: 3 }}><ScheduleIcon style={{ fontSize: 12 }} />{due.label}</span>
            ) : (card.dueDate ? (
              <span style={{ fontSize: 11, color: "#78909c", display: "inline-flex", alignItems: "center", gap: 3 }}><EventIcon style={{ fontSize: 13 }} />{fmtDateBR(card.dueDate)}</span>
            ) : null)}
            {prog.total > 0 ? <span style={{ fontSize: 11, color: prog.pct === 100 ? "#2e7d32" : "#78909c", display: "inline-flex", alignItems: "center", gap: 3 }}><CheckBoxOutlinedIcon style={{ fontSize: 13 }} />{prog.done}/{prog.total}</span> : null}
            {card.commentCount > 0 ? <span style={{ fontSize: 11, color: "#78909c", display: "inline-flex", alignItems: "center", gap: 3 }}><ChatBubbleOutlineIcon style={{ fontSize: 13 }} />{card.commentCount}</span> : null}
            {card.attachmentCount > 0 ? <span style={{ fontSize: 11, color: "#78909c", display: "inline-flex", alignItems: "center", gap: 3 }}><AttachFileIcon style={{ fontSize: 13 }} />{card.attachmentCount}</span> : null}
            {presentLinks.map((k) => <span key={k.kind} title={k.label} style={{ fontSize: 12 }}>{k.icon}</span>)}
            <Box flexGrow={1} />
            {memberIds.slice(0, 3).map((id) => {
              const u = users.find((x) => Number(x.id) === Number(id));
              return <UserAvatar key={id} user={u || { id }} companyId={user.companyId} size={22} title={u ? u.name : `#${id}`} />;
            })}
          </Box>
        </div>
      </>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Tooltip title="Voltar aos quadros"><IconButton size="small" onClick={onBack}><ArrowBackIcon /></IconButton></Tooltip>
          <Title>{board.name}</Title>
        </Box>
        <Box flexGrow={1} />
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Tooltip title="Quadro"><IconButton size="small" color={view === "board" ? "primary" : "default"} onClick={() => setView("board")}><ViewColumnIcon /></IconButton></Tooltip>
          <Tooltip title="Calendário"><IconButton size="small" color={view === "calendar" ? "primary" : "default"} onClick={() => setView("calendar")}><CalendarTodayIcon /></IconButton></Tooltip>
          <Tooltip title="Tabela"><IconButton size="small" color={view === "table" ? "primary" : "default"} onClick={() => setView("table")}><TableChartIcon /></IconButton></Tooltip>
          <Tooltip title="Fundo do quadro"><IconButton size="small" onClick={() => setBgDialogOpen(true)}><WallpaperIcon /></IconButton></Tooltip>
          <Tooltip title="Etiquetas"><IconButton size="small" onClick={() => setLabelMgrOpen(true)}><LabelIcon /></IconButton></Tooltip>
          <Tooltip title="Automações"><IconButton size="small" onClick={() => setAutoOpen(true)}><FlashOnIcon /></IconButton></Tooltip>
          <NotificationsBell />
        </Box>
      </MainHeader>

      {/* Filtros + stats */}
      <Box className={classes.filters}>
        <TextField size="small" placeholder="Buscar card..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 160 }} />
        <TextField select size="small" label="Membro" value={filterMember} onChange={(e) => setFilterMember(e.target.value)} style={{ minWidth: 130 }}>
          <MenuItem value="">Todos</MenuItem>
          {users.map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Prioridade" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} style={{ minWidth: 130 }}>
          <MenuItem value="">Todas</MenuItem>
          {PRIORITIES.filter((p) => p.value).map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </TextField>
        <Box display="flex" alignItems="center"><Checkbox size="small" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /><Typography variant="caption">Concluídos</Typography></Box>
        <Box display="flex" alignItems="center"><Checkbox size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /><Typography variant="caption">Arquivados</Typography></Box>
        <Box flexGrow={1} />
        <Box className={classes.statsBar}>
          <Chip size="small" label={`${stats.total} cards`} />
          <Chip size="small" label={`${stats.done} concluídos`} style={{ background: "#e8f5e9", color: "#2e7d32" }} />
          {stats.overdue > 0 && <Chip size="small" label={`${stats.overdue} atrasados`} style={{ background: "#ffebee", color: "#c62828" }} />}
          {pipelineValue > 0 && <Chip size="small" label={`💰 ${fmtMoneyBR(pipelineValue)}`} style={{ background: "#e8f5e9", color: "#2e7d32" }} />}
        </Box>
      </Box>

      <Paper variant="outlined" className={classes.mainPaper}>
        {view === "calendar" ? (
          <Box style={{ flex: 1, overflowY: "auto", padding: 8 }}><CalendarView board={viewBoard} onOpenCard={setSelectedCard} /></Box>
        ) : view === "table" ? (
          <Box style={{ flex: 1, overflowY: "auto", padding: 8 }}><TableView board={viewBoard} users={users} onOpenCard={setSelectedCard} /></Box>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="board-lists" direction="horizontal" type="COLUMN">
              {(prov) => (
                <div className={classes.board} ref={prov.innerRef} {...prov.droppableProps} style={boardContainerStyle}>
                  {lists.map((list, idx) => {
                    const visibleCards = (list.cards || []).filter(matches);
                    const colValue = visibleCards.reduce((s, c) => s + (Number(c.quoteTotal) || 0), 0);
                    const isCollapsed = collapsed.has(list.id);
                    return (
                      <Draggable draggableId={`col-${list.id}`} index={idx} key={list.id}>
                        {(colProv) => (isCollapsed ? (
                          <div ref={colProv.innerRef} {...colProv.draggableProps} {...colProv.dragHandleProps}
                            className={classes.collapsedCol} style={{ ...colProv.draggableProps.style, background: colBg }}
                            onClick={() => toggleCollapse(list.id)} title={`Expandir "${list.name}"`}>
                            <Typography variant="caption" color="textSecondary">{visibleCards.length}</Typography>
                            <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", flex: 1 }}>{list.name}</div>
                          </div>
                        ) : (
                          <div ref={colProv.innerRef} {...colProv.draggableProps} className={classes.column} style={{ ...colProv.draggableProps.style, background: colBg }}>
                            <div className={classes.colHeader}>
                              <span {...colProv.dragHandleProps} style={{ borderLeft: `3px solid ${list.color || "#90a4ae"}`, paddingLeft: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "grab" }}>
                                {list.name}{" "}
                                <Typography component="span" variant="caption" color="textSecondary">({visibleCards.length})</Typography>
                                {colValue > 0 ? <Typography component="span" variant="caption" style={{ color: "#2e7d32", marginLeft: 4 }}>· {fmtMoneyBR(colValue)}</Typography> : null}
                              </span>
                              <Box display="flex" alignItems="center">
                                <Tooltip title="Recolher"><IconButton size="small" onClick={() => toggleCollapse(list.id)}><ChevronLeftIcon fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Mais"><IconButton size="small" onClick={(e) => openListMenu(e, list)}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>
                              </Box>
                            </div>
                            <Droppable droppableId={`list-${list.id}`} type="CARD">
                              {(cardProv, snap) => (
                                <div ref={cardProv.innerRef} {...cardProv.droppableProps} className={classes.cards}
                                  style={{ background: snap.isDraggingOver ? "rgba(21,101,192,0.06)" : undefined }}>
                                  {visibleCards.map((card, ci) => (
                                    <Draggable draggableId={`card-${card.id}`} index={ci} key={card.id} isDragDisabled={filtersActive}>
                                      {(cp) => (
                                        <div ref={cp.innerRef} {...cp.draggableProps} {...cp.dragHandleProps}
                                          className={classes.card} onClick={() => setSelectedCard(card)} style={cp.draggableProps.style}>
                                          {renderCardFace(card)}
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {cardProv.placeholder}
                                  {addingCardListId === list.id ? (
                                    <TextField autoFocus size="small" variant="outlined" placeholder="Título do card (Enter)"
                                      value={newCardTitle} onChange={(e) => setNewCardTitle(e.target.value)}
                                      onKeyDown={(e) => { if (e.key === "Enter") handleAddCard(list.id); if (e.key === "Escape") { setAddingCardListId(null); setNewCardTitle(""); } }}
                                      onBlur={() => { setAddingCardListId(null); setNewCardTitle(""); }} fullWidth />
                                  ) : (
                                    <div className={classes.addCard} onClick={() => { setAddingCardListId(list.id); setNewCardTitle(""); }}>+ Adicionar card</div>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        ))}
                      </Draggable>
                    );
                  })}
                  {prov.placeholder}
                  <div className={classes.addColumn}>
                    {addingColumn ? (
                      <TextField autoFocus size="small" variant="outlined" placeholder="Nome da coluna (Enter)"
                        value={newColumnName} onChange={(e) => setNewColumnName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddColumn(); if (e.key === "Escape") { setAddingColumn(false); setNewColumnName(""); } }}
                        onBlur={handleAddColumn} fullWidth />
                    ) : (
                      <Button startIcon={<AddIcon />} onClick={() => { setAddingColumn(true); setNewColumnName(""); }}>Adicionar coluna</Button>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Paper>

      <CardModal
        open={!!selectedCard}
        card={selectedCard}
        users={users}
        boardLabels={board.labels}
        onClose={() => setSelectedCard(null)}
        onChanged={() => { setSelectedCard(null); fetchBoard(); }}
        onLinksChanged={fetchBoard}
      />
      <LabelManagerDialog open={labelMgrOpen} board={board} onClose={() => setLabelMgrOpen(false)} onSaved={() => fetchBoard()} />
      <AutomationsDialog open={autoOpen} board={board} onClose={() => setAutoOpen(false)} />
      <BoardBackgroundDialog open={bgDialogOpen} board={board} onClose={() => setBgDialogOpen(false)} onSaved={() => fetchBoard()} />
      <Menu anchorEl={listMenuAnchor} open={!!listMenuAnchor} onClose={closeListMenu} getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuItem onClick={() => handleRenameList(listMenuList)}>Renomear coluna</MenuItem>
        <MenuItem onClick={() => handleArchiveListCards(listMenuList)}>Arquivar todos os cards</MenuItem>
        <MenuItem onClick={() => { const l = listMenuList; closeListMenu(); handleRemoveList(l); }} style={{ color: "#c62828" }}>Excluir coluna</MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default BoardView;
